import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'
import axiosInstance from '../utils/axios'

export const useDevProfilesStore = defineStore('devProfiles', () => {
  const uiStore = useUIStore()

  // State
  const devProfiles = ref([])
  const currentProfile = ref(null)
  const loading = ref(false)

  // API Functions
  const fetchDevProfiles = async (all = false) => {
    try {
      loading.value = true
      logService.info(`Fetching dev profiles (all=${all})...`)

      const endpoint = all ? '/admin/dev-profiles' : '/dev-profiles'
      const response = await apiService.get(endpoint)

      if (response.success) {
        devProfiles.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch dev profiles')
      }
    } catch (error) {
      logService.error('Fetch Dev Profiles Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    } finally {
      loading.value = false
    }
  }

  const fetchDevProfile = async (idOrSlug) => {
    try {
      loading.value = true
      logService.info(`Fetching dev profile: ${idOrSlug}`)

      const response = await apiService.get(`/dev-profiles/${idOrSlug}`)

      if (response.success) {
        currentProfile.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Dev profile not found')
      }
    } catch (error) {
      logService.error(`Fetch Dev Profile Error [${idOrSlug}]:`, error)
      return null
    } finally {
      loading.value = false
    }
  }

  const createDevProfile = async (profileData) => {
    try {
      loading.value = true
      logService.info('Creating dev profile...', profileData)

      const response = await apiService.post('/dev-profiles', profileData)

      if (response.success) {
        devProfiles.value.push(response.data)
        uiStore.showNotification({
          type: 'success',
          title: 'Dev Profile Created',
          message: 'Profile has been created successfully.'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to create profile')
      }
    } catch (error) {
      logService.error('Create Dev Profile Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updateDevProfile = async (id, profileData) => {
    try {
      loading.value = true
      logService.info(`Updating dev profile: ${id}`, profileData)

      const response = await apiService.put(`/dev-profiles/${id}`, profileData)

      if (response.success) {
        const index = devProfiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          devProfiles.value[index] = response.data
        }
        uiStore.showNotification({
          type: 'success',
          title: 'Dev Profile Updated',
          message: 'Profile has been updated successfully.'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to update profile')
      }
    } catch (error) {
      logService.error(`Update Dev Profile Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const deleteDevProfile = async (id) => {
    try {
      loading.value = true
      logService.info(`Deleting dev profile: ${id}`)

      const response = await apiService.delete(`/dev-profiles/${id}`)

      if (response.success) {
        devProfiles.value = devProfiles.value.filter(p => p.id !== id)
        uiStore.showNotification({
          type: 'success',
          title: 'Dev Profile Deleted',
          message: 'Profile has been deleted successfully.'
        })
        return true
      } else {
        throw new Error(response.message || 'Failed to delete profile')
      }
    } catch (error) {
      logService.error(`Delete Dev Profile Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: error.message
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const uploadDevProfileImage = async (id, file, type = 'profile') => {
    try {
      logService.info(`Uploading ${type} image for dev profile: ${id}`)
      const formData = new FormData()
      formData.append('image', file)
      formData.append('type', type)

      const response = await apiService.upload(`/dev-profiles/${id}/upload-image`, formData)

      if (response.success) {
        const index = devProfiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          if (type === 'cover') {
            devProfiles.value[index].cover_image = response.data.image
            devProfiles.value[index].cover_image_url = response.data.image_url
          } else {
            devProfiles.value[index].profile_image = response.data.image
            devProfiles.value[index].profile_image_url = response.data.image_url
          }
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Image Uploaded',
          message: `${type.charAt(0).toUpperCase() + type.slice(1)} image uploaded successfully.`
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload image')
      }
    } catch (error) {
      logService.error(`Upload Image Error [${id}, ${type}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Upload Failed',
        message: error.message
      })
      return null
    }
  }

  const toggleDevProfileActive = async (id) => {
    try {
      logService.info(`Toggling active status for dev profile: ${id}`)
      const response = await apiService.post(`/dev-profiles/${id}/toggle-active`)

      if (response.success) {
        const index = devProfiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          devProfiles.value[index].is_active = response.data.is_active
        }
        return response.data
      } else {
        throw new Error(response.message || 'Failed to toggle status')
      }
    } catch (error) {
      logService.error(`Toggle status Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Action Failed',
        message: error.message
      })
      return null
    }
  }

  // Helper functions for image URLs
  const getImageUrl = (path) => {
    if (!path) return null
    if (path.startsWith('http')) return path
    const axiosBaseUrl = axiosInstance.defaults.baseURL || 'http://localhost:8000/api/v1'
    const baseUrl = axiosBaseUrl.replace('/api/v1', '')
    return `${baseUrl}/storage/${path}`
  }

  return {
    devProfiles,
    currentProfile,
    loading,
    fetchDevProfiles,
    fetchDevProfile,
    createDevProfile,
    updateDevProfile,
    deleteDevProfile,
    uploadDevProfileImage,
    toggleDevProfileActive,
    getImageUrl
  }
})
